class PhotoUploadBlock extends HTMLElement {
  static SESSION_KEY = 'kunstpfote_session';
  static UPLOAD_DATA_KEY = 'kunstpfote_uploads';
  static MAX_FILES = 4;
  static MAX_SIZE = 10 * 1024 * 1024;
  static ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  constructor() {
    super();
    this.entries = [];
    // Serializes upload batches: each batch waits for the previous to finish
    // so the sessionId is always in localStorage before the next batch reads it
    this._uploadQueue = Promise.resolve();
  }

  connectedCallback() {
    this.dropzone = this.querySelector('.photo-upload__dropzone');
    this.fileInput = this.querySelector('.photo-upload__input');
    this.previewGrid = this.querySelector('.photo-upload__preview-grid');
    this.messagesEl = this.querySelector('.photo-upload__messages');
    this.generateBtn = this.querySelector('.photo-upload__generate-btn');
    this.lightbox = this.querySelector('.photo-upload__lightbox');
    this.lightboxImg = this.querySelector('.photo-upload__lightbox-img');

    this.lightbox?.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.lightbox.close();
    });
    this.querySelector('.photo-upload__lightbox-close')?.addEventListener('click', () => this.lightbox.close());

    this.dropzone.addEventListener('click', () => {
      if (!this.dropzone.classList.contains('photo-upload__dropzone--full')) {
        this.fileInput.click();
      }
    });

    this.dropzone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', (e) => {
      this.handleFiles(e.target.files);
      this.fileInput.value = '';
    });

    this.dropzone.addEventListener('dragenter', (e) => e.preventDefault());

    this.dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      this.dropzone.classList.add('photo-upload__dropzone--active');
    });

    this.dropzone.addEventListener('dragleave', (e) => {
      if (!this.dropzone.contains(e.relatedTarget)) {
        this.dropzone.classList.remove('photo-upload__dropzone--active');
      }
    });

    this.dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzone.classList.remove('photo-upload__dropzone--active');
      this.handleFiles(e.dataTransfer.files);
    });

    this.generateBtn.addEventListener('click', () => this.handleGenerate());

    this.restoreSession();

    // Re-evaluate button + generated image when the variant changes
    const productDetails = document.querySelector('.product-details');
    if (productDetails) {
      new MutationObserver(() => {
        this.updateStatus();
        this.restoreGeneratedImage();
      }).observe(productDetails, { attributes: true, attributeFilter: ['data-sku'] });
    }

    // Sync with photo management modal
    window.addEventListener('kunstpfote:photo-removed', (e) => {
      const { filename } = e.detail;
      const idx = this.entries.findIndex((entry) => entry.filename === filename);
      if (idx === -1) return;
      const entry = this.entries[idx];
      if (entry.file) URL.revokeObjectURL(entry.imgSrc);
      this.entries.splice(idx, 1);
      this.previewGrid.querySelector(`[data-id="${entry.id}"]`)?.remove();
      this.updateDropzone();
      this.updateStatus();
    });

    window.addEventListener('kunstpfote:photos-uploaded', (e) => {
      const { sessionId, files, uploadBase } = e.detail;
      for (const filename of files) {
        if (this.entries.some((entry) => entry.filename === filename)) continue;
        const entry = {
          id: crypto.randomUUID(),
          imgSrc: `${uploadBase}/image/${sessionId}/originals/${filename}`,
          filename,
          state: 'done',
        };
        this.entries.push(entry);
        this.addPreviewItem(entry);
      }
      this.updateDropzone();
      this.updateStatus();
    });

    window.addEventListener('kunstpfote:generation-complete', (e) => {
      const { sku: generatedSku, sessionId, uploadBase } = e.detail;
      if (!generatedSku || generatedSku !== this.getSku()) return;
      this.showGeneratedImage(`${uploadBase}/image/${sessionId}/generated/${generatedSku}.jpg`);
    });
  }

  // ─── Session persistence ───────────────────────────────────────────────────

  getUploadBase() {
    return this.dataset.uploadEndpoint.replace(/\/upload$/, '');
  }

  getSku() {
    return document.querySelector('.product-details')?.dataset.sku
      || this.dataset.motiv
      || '';
  }

  buildImageUrl(sessionId, type, filename) {
    return `${this.getUploadBase()}/image/${sessionId}/${type}/${filename}`;
  }

  readStoredData() {
    try {
      const raw = localStorage.getItem(PhotoUploadBlock.UPLOAD_DATA_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data.sessionId || !Array.isArray(data.originals)) return null;
      return data;
    } catch {
      return null;
    }
  }

  writeStoredData(data) {
    if (!data.uploadBase) {
      data.uploadBase = this.getUploadBase();
    }
    localStorage.setItem(PhotoUploadBlock.UPLOAD_DATA_KEY, JSON.stringify(data));
  }

  restoreSession() {
    const data = this.readStoredData();
    if (!data || data.originals.length === 0) return;

    for (const filename of data.originals) {
      const entry = {
        id: crypto.randomUUID(),
        imgSrc: this.buildImageUrl(data.sessionId, 'originals', filename),
        filename,
        state: 'done',
      };
      this.entries.push(entry);
      this.addPreviewItem(entry);
    }

    this.updateDropzone();
    this.updateStatus();
    this.restoreGeneratedImage();
  }

  restoreGeneratedImage() {
    this._clearGeneratedItem();

    const sku = this.getSku();
    if (!sku) return;
    const data = this.readStoredData();
    if (!data?.sessionId) return;

    const imgUrl = this.buildImageUrl(data.sessionId, 'generated', `${sku}.jpg`);

    const storedSkus = this._normaliseGeneratedSkus(data.generated);

    if (storedSkus.includes(sku)) {
      this._insertGeneratedItem(imgUrl, sku);
    } else {
      // Probe server – covers sessions generated before localStorage tracking was added
      const probe = new Image();
      probe.onload = () => {
        const fresh = this.readStoredData();
        if (fresh) {
          fresh.generated = fresh.generated ?? [];
          if (!this._normaliseGeneratedSkus(fresh.generated).includes(sku)) {
            fresh.generated.push({ sku, url: window.location.pathname + window.location.search });
          }
          this.writeStoredData(fresh);
        }
        this._insertGeneratedItem(imgUrl, sku);
      };
      probe.src = `${imgUrl}?_t=${Date.now()}`;
    }
  }

  showGeneratedImage(imgUrl) {
    const sku = this.getSku();
    this._clearGeneratedItem();
    this._insertGeneratedItem(imgUrl, sku);
  }

  _insertGeneratedItem(imgUrl, sku) {
    const li = document.createElement('li');
    li.className = 'photo-upload__preview-item photo-upload__preview-item--generated';
    li.dataset.generatedSku = sku || '';
    li.innerHTML = `
      <img class="photo-upload__preview-img" src="${imgUrl}" alt="Dein Kunstporträt">
      <button class="photo-upload__zoom-btn" type="button" aria-label="Vorschau öffnen" title="Vorschau öffnen">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
      </button>
    `;

    li.querySelector('.photo-upload__zoom-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      this.lightboxImg.src = imgUrl;
      this.lightbox.showModal();
    });

    li.querySelector('.photo-upload__preview-img').addEventListener('error', () => {
      if (sku) {
        const data = this.readStoredData();
        if (data) {
          data.generated = (data.generated ?? []).filter((e) => this._skuFromEntry(e) !== sku);
          this.writeStoredData(data);
        }
      }
      li.remove();
      this._updateGridModifier();
    }, { once: true });

    this.previewGrid.prepend(li);
    this._updateGridModifier();
    document.querySelector('.product-details')?.classList.add('kp-generated');
    this.querySelector('[data-generated-cta]')?.removeAttribute('hidden');
  }

  _skuFromEntry(entry) {
    if (typeof entry === 'string') return entry.replace(/\.jpg$/i, '') || null;
    return (entry && typeof entry.sku === 'string') ? entry.sku : null;
  }

  _normaliseGeneratedSkus(arr) {
    return (arr ?? [])
      .map((e) => this._skuFromEntry(e))
      .filter((s) => s && s !== 'undefined');
  }

  _clearGeneratedItem() {
    this.previewGrid.querySelector('.photo-upload__preview-item--generated')?.remove();
    this._updateGridModifier();
    document.querySelector('.product-details')?.classList.remove('kp-generated');
    this.querySelector('[data-generated-cta]')?.setAttribute('hidden', '');
  }

  _updateGridModifier() {
    const has = Boolean(this.previewGrid.querySelector('.photo-upload__preview-item--generated'));
    this.previewGrid.classList.toggle('photo-upload__preview-grid--has-generated', has);
  }

  appendToStoredData(sessionId, newFilenames) {
    const existing = this.readStoredData() ?? {};
    this.writeStoredData({
      ...existing,
      sessionId,
      originals: [...(existing.originals ?? []), ...newFilenames],
    });
  }

  removeFromStoredData(filename) {
    const data = this.readStoredData();
    if (!data) return;
    data.originals = data.originals.filter((f) => f !== filename);
    this.writeStoredData(data);
  }

  // ─── File handling ─────────────────────────────────────────────────────────

  handleFiles(fileList) {
    this.clearErrors();

    const remaining = PhotoUploadBlock.MAX_FILES - this.entries.length;
    if (remaining <= 0) {
      this.showError('Du hast bereits 5 Fotos hinzugefügt. Entferne ein Foto, um ein neues hochzuladen.');
      return;
    }

    const errors = [];
    const valid = [];

    for (const file of Array.from(fileList)) {
      if (!PhotoUploadBlock.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`„${file.name}": Nicht unterstütztes Format – bitte JPG, PNG oder WebP verwenden.`);
        continue;
      }
      if (file.size > PhotoUploadBlock.MAX_SIZE) {
        errors.push(`„${file.name}": Datei zu groß (max. 10 MB erlaubt).`);
        continue;
      }
      valid.push(file);
    }

    const toAdd = valid.slice(0, remaining);
    if (valid.length > remaining) {
      errors.push(`Maximal ${PhotoUploadBlock.MAX_FILES} Fotos erlaubt – ${valid.length - remaining} Foto(s) wurden übersprungen.`);
    }

    errors.forEach((msg) => this.showError(msg));

    if (toAdd.length === 0) return;

    const newEntries = toAdd.map((file) => {
      const entry = {
        id: crypto.randomUUID(),
        file,
        imgSrc: URL.createObjectURL(file),
        filename: null,
        state: 'uploading',
      };
      this.entries.push(entry);
      this.addPreviewItem(entry);
      return entry;
    });

    this._uploadQueue = this._uploadQueue.then(() => this.uploadBatch(newEntries));

    this.updateDropzone();
  }

  // ─── Upload ────────────────────────────────────────────────────────────────

  async uploadBatch(entries) {
    const formData = new FormData();
    for (const entry of entries) {
      formData.append('photos', entry.file);
    }

    const sessionId = localStorage.getItem(PhotoUploadBlock.SESSION_KEY);
    if (sessionId) formData.append('sessionId', sessionId);

    const motiv = this.getSku();
    if (motiv) formData.append('motiv', motiv);

    try {
      const res = await fetch(this.dataset.uploadEndpoint, { method: 'POST', body: formData });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Serverfehler (${res.status})`);
      }

      const data = await res.json();
      const newSessionId = data.sessionId;

      if (newSessionId) {
        localStorage.setItem(PhotoUploadBlock.SESSION_KEY, newSessionId);
      }

      // Map returned filenames back to entries in order
      data.files.forEach((filename, i) => {
        const entry = entries[i];
        if (!entry) return;
        entry.filename = filename;
        entry.imgSrc = this.buildImageUrl(newSessionId, 'originals', filename);
        entry.state = 'done';
        this.updatePreviewItem(entry);
      });

      // Persist filenames so they survive page reload
      this.appendToStoredData(newSessionId, data.files);

      // Let other components (step cards, nav badge, …) pick up the new photos
      window.dispatchEvent(new CustomEvent('kunstpfote:photos-uploaded', {
        detail: { sessionId: newSessionId, files: data.files, uploadBase: this.getUploadBase() },
      }));
    } catch (err) {
      for (const entry of entries) {
        entry.state = 'error';
        this.updatePreviewItem(entry);
        // Free blob memory for failed uploads
        if (entry.file) URL.revokeObjectURL(entry.imgSrc);
      }
      this.showError(err.message || 'Upload fehlgeschlagen. Bitte versuche es erneut.');
    }

    this.updateStatus();
  }

  // ─── Preview items ─────────────────────────────────────────────────────────

  addPreviewItem(entry) {
    const li = document.createElement('li');
    li.className = 'photo-upload__preview-item';
    li.dataset.id = entry.id;

    const isUploading = entry.state === 'uploading';
    li.innerHTML = `
      <img src="${entry.imgSrc}" class="photo-upload__preview-img" alt="" loading="lazy">
      ${isUploading ? '<div class="photo-upload__preview-overlay"><span class="photo-upload__spinner"></span></div>' : ''}
      <button class="photo-upload__remove-btn" type="button" aria-label="Bild entfernen" title="Bild entfernen">
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>
    `;
    li.querySelector('.photo-upload__remove-btn').addEventListener('click', () => this.removeEntry(entry.id));

    // Silently clean up if the server image was deleted externally (no HTTP DELETE needed)
    if (!isUploading) {
      li.querySelector('.photo-upload__preview-img')?.addEventListener(
        'error',
        () => this.handleBrokenEntry(entry.id),
        { once: true }
      );
    }

    this.previewGrid.appendChild(li);
  }

  updatePreviewItem(entry) {
    const li = this.previewGrid.querySelector(`[data-id="${entry.id}"]`);
    if (!li) return;

    const img = li.querySelector('.photo-upload__preview-img');
    if (img && entry.imgSrc) img.src = entry.imgSrc;

    // Get or create overlay only when needed
    const getOrCreateOverlay = () => {
      let overlay = li.querySelector('.photo-upload__preview-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        li.insertBefore(overlay, li.querySelector('.photo-upload__remove-btn'));
      }
      return overlay;
    };

    if (entry.state === 'done') {
      const overlay = getOrCreateOverlay();
      overlay.className = 'photo-upload__preview-overlay photo-upload__preview-overlay--done';
      overlay.innerHTML = `
        <svg class="photo-upload__checkmark" width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
          <path d="M4 11L9 16L18 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>`;
      setTimeout(() => {
        overlay.style.transition = 'opacity 0.4s ease';
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 400);
      }, 1400);
    } else if (entry.state === 'error') {
      const overlay = getOrCreateOverlay();
      overlay.className = 'photo-upload__preview-overlay photo-upload__preview-overlay--error';
      overlay.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
          <path d="M2 2L16 16M16 2L2 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>`;
    }
  }

  async removeEntry(id) {
    const idx = this.entries.findIndex((e) => e.id === id);
    if (idx === -1) return;
    const entry = this.entries[idx];

    // Server delete for confirmed uploads only
    if (entry.filename && entry.state === 'done') {
      const sessionId = localStorage.getItem(PhotoUploadBlock.SESSION_KEY);
      if (sessionId) {
        const li = this.previewGrid.querySelector(`[data-id="${id}"]`);
        const btn = li?.querySelector('.photo-upload__remove-btn');
        if (btn) btn.disabled = true;

        try {
          const url = `${this.getUploadBase()}/image/${sessionId}/originals/${entry.filename}`;
          const res = await fetch(url, { method: 'DELETE' });
          // 404 means already gone – treat as success
          if (!res.ok && res.status !== 404) {
            if (btn) btn.disabled = false;
            this.showError(
              res.status === 429
                ? 'Zu viele Anfragen – bitte kurz warten.'
                : `Löschen fehlgeschlagen (${res.status}).`
            );
            return;
          }
        } catch {
          if (btn) btn.disabled = false;
          this.showError('Löschen fehlgeschlagen. Bitte erneut versuchen.');
          return;
        }
      }
    }

    if (entry.file) URL.revokeObjectURL(entry.imgSrc);

    // Splice from entries BEFORE dispatching so the event listener on this same
    // component finds idx === -1 and skips, preventing double-processing.
    this.entries.splice(idx, 1);
    this.previewGrid.querySelector(`[data-id="${id}"]`)?.remove();
    this.updateDropzone();
    this.updateStatus();

    if (entry.filename) {
      this.removeFromStoredData(entry.filename);
      window.dispatchEvent(new CustomEvent('kunstpfote:photo-removed', { detail: { filename: entry.filename } }));
    }
  }

  handleBrokenEntry(id) {
    const idx = this.entries.findIndex((e) => e.id === id);
    if (idx === -1) return;
    const entry = this.entries[idx];
    if (entry.filename) this.removeFromStoredData(entry.filename);
    this.entries.splice(idx, 1);
    this.previewGrid.querySelector(`[data-id="${id}"]`)?.remove();
    this.updateDropzone();
    this.updateStatus();
  }

  // ─── UI state ──────────────────────────────────────────────────────────────

  updateDropzone() {
    const isFull = this.entries.length >= PhotoUploadBlock.MAX_FILES;
    this.dropzone.classList.toggle('photo-upload__dropzone--full', isFull);
  }

  updateStatus() {
    const uploading = this.entries.some((e) => e.state === 'uploading');
    const done = this.entries.filter((e) => e.state === 'done');
    const hasSku = Boolean(this.getSku());

    if (uploading) {
      this.generateBtn.hidden = true;
      return;
    }

    this.generateBtn.hidden = !(done.length > 0 && hasSku);
  }

  clearErrors() {
    this.messagesEl.querySelectorAll('.photo-upload__message--error').forEach((el) => el.remove());
  }

  showError(msg) {
    const p = document.createElement('p');
    p.className = 'photo-upload__message photo-upload__message--error';
    p.textContent = msg;
    this.messagesEl.appendChild(p);
  }

  setSuccess(msg) {
    this.messagesEl.querySelectorAll('.photo-upload__message--success').forEach((el) => el.remove());
    const p = document.createElement('p');
    p.className = 'photo-upload__message photo-upload__message--success';
    p.textContent = msg;
    this.messagesEl.appendChild(p);
  }

  handleGenerate() {
    const sessionId = localStorage.getItem(PhotoUploadBlock.SESSION_KEY);
    if (!sessionId) {
      this.showError('Keine aktive Sitzung – bitte zuerst Fotos hochladen.');
      return;
    }

    this.generateBtn.disabled = true;

    window.dispatchEvent(new CustomEvent('kunstpfote:generation-started', {
      detail: {
        sessionId,
        sku: this.getSku(),
        uploadBase: this.getUploadBase(),
        productUrl: window.location.pathname + window.location.search,
      },
    }));
  }
}

customElements.define('photo-upload-block', PhotoUploadBlock);
